import {
  DOCUMENT_EXPORT_ARCHIVE_LIMIT,
  DOCUMENT_EXPORT_FILE_LIMIT,
  type DocumentExportFormat,
  type DocumentExportSkipReason,
} from '@kanbu/schema/contracts';
import JSZip from 'jszip';

import { trpcClient } from '@/services/trpc';

export type ExportDocument = { id: string; name?: string };
export type DocumentExportSummary = {
  total: number;
  downloaded: number;
  skipped: Array<ExportDocument & { reason: DocumentExportSkipReason }>;
};

function safeFileName(name: string): string {
  return (
    name
      .replaceAll(/[\u0000-\u001F"*/:<>?\\|]/g, '_')
      .replace(/^\.+/, '')
      .trim()
      .slice(0, 180) || 'document'
  );
}

function archiveFileName(name: string, id: string): string {
  const extensionIndex = name.lastIndexOf('.');

  return extensionIndex > 0
    ? `${name.slice(0, extensionIndex)}-${id}${name.slice(extensionIndex)}`
    : `${name}-${id}`;
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = name;
  window.document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportDocuments({
  documents,
  format,
  onProgress,
}: {
  documents: ExportDocument[];
  format: DocumentExportFormat;
  onProgress?: (completed: number) => void;
}): Promise<DocumentExportSummary> {
  const selection = [
    ...new Map(documents.map(document => [document.id, document])).values(),
  ];
  const summary: DocumentExportSummary = {
    total: selection.length,
    downloaded: 0,
    skipped: [],
  };
  const files: Array<{
    id: string;
    name: string;
    bytes: Uint8Array<ArrayBuffer>;
    mimeType: string;
  }> = [];
  let totalBytes = 0;
  let completed = 0;
  onProgress?.(completed);

  for (const document of selection) {
    let name = document.name;

    try {
      const maxBytes = Math.min(
        DOCUMENT_EXPORT_FILE_LIMIT,
        DOCUMENT_EXPORT_ARCHIVE_LIMIT - totalBytes,
      );

      if (maxBytes <= 0) {
        summary.skipped.push({ ...document, reason: 'too_large' });
        continue;
      }

      let bytes: Uint8Array<ArrayBuffer>;
      let fileName: string;
      let mimeType: string;

      if (format === 'source') {
        const result = await trpcClient.documentExports.source.mutate({
          documentId: document.id,
          maxBytes,
        });
        name = result.name;

        if (result.status === 'skipped') {
          summary.skipped.push({ ...document, name, reason: result.reason });
          continue;
        }

        bytes = Uint8Array.from(
          atob(result.data),
          char => char.codePointAt(0)!,
        );
        fileName = result.fileName;
        mimeType = result.mimeType;
      } else {
        const result = await trpcClient.documents.findOne.query({
          id: document.id,
          populate: ['content'],
        });
        name = result?.name || name;
        const content = result?.content?.pages
          .map(page => page.markdown)
          .join('\n\n');

        if (!content?.trim()) {
          summary.skipped.push({ ...document, name, reason: 'empty' });
          continue;
        }

        bytes = new TextEncoder().encode(content);
        fileName = `${name || 'document'}.md`;
        mimeType = 'text/markdown;charset=utf-8';
      }

      if (bytes.length > maxBytes) {
        summary.skipped.push({ ...document, name, reason: 'too_large' });
        continue;
      }

      files.push({
        id: document.id,
        name: safeFileName(fileName),
        bytes,
        mimeType,
      });
      totalBytes += bytes.length;
    } catch {
      summary.skipped.push({ ...document, name, reason: 'unavailable' });
    } finally {
      onProgress?.(++completed);
    }
  }

  if (files.length === 0) return summary;

  if (selection.length === 1) {
    saveBlob(
      new Blob([files[0].bytes], { type: files[0].mimeType }),
      files[0].name,
    );
  } else {
    const zip = new JSZip();

    for (const file of files) {
      zip.file(archiveFileName(file.name, file.id), file.bytes);
    }

    saveBlob(await zip.generateAsync({ type: 'blob' }), `kanbu-${format}.zip`);
  }

  summary.downloaded = files.length;

  return summary;
}
